import React from 'react';

/**
 * Msg #975 — Daily Quest Board.
 * Pulls /api/quests/today for the logged-in user, shows progress bars per
 * quest, and lets them CLAIM completed ones for a reward (extra kill credit
 * or a small HP grant).  Quests refresh at 00:00 UTC.
 */
export default function DailyQuestModal({ open, onClose, apiBase, userId, onClaimed }) {
  const [board, setBoard] = React.useState(null);
  const [busyKey, setBusyKey] = React.useState('');
  const [err, setErr] = React.useState('');

  const load = React.useCallback(async () => {
    if (!apiBase || !userId) return;
    try {
      const r = await fetch(`${apiBase}/quests/today?user_id=${encodeURIComponent(userId)}`);
      const d = await r.json();
      setBoard(d); setErr('');
    } catch (e) { setErr(String(e.message || e)); }
  }, [apiBase, userId]);

  React.useEffect(() => { if (open) load(); }, [open, load]);
  React.useEffect(() => {
    if (!open) return;
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [open, load]);

  if (!open) return null;

  const claim = async (key) => {
    setBusyKey(key);
    try {
      const r = await fetch(`${apiBase}/quests/claim`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, key }),
      });
      const d = await r.json();
      if (!r.ok) { setErr(d.detail || 'Claim failed'); return; }
      if (onClaimed) onClaimed(d);
      await load();
    } finally { setBusyKey(''); }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(20,40,20,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      data-testid="daily-quest-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="room-themed-panel"
        style={{
          width: 520, maxWidth: '95vw',
          border: '2px solid var(--room-border, #5a4a1d)',
          padding: '18px 22px',
          fontFamily: 'Georgia, "Times New Roman", serif',
          boxShadow: '0 8px 28px rgba(0,0,0,0.45)',
        }}
      >
        <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 20, letterSpacing: 2 }}>
          📜 Daily Quest Board
        </div>
        <div style={{ textAlign: 'center', fontSize: 11, marginTop: 4, opacity: 0.8 }}>
          Fresh quests roll at midnight UTC. Complete them for kill credit or HP.
        </div>

        {err && <div style={{ color: '#7a1f1f', marginTop: 8 }}>{err}</div>}

        <div style={{ marginTop: 14 }}>
          {(board && board.quests || []).map((q) => {
            const pct = q.target ? Math.min(100, (q.progress / q.target) * 100) : 0;
            const done = q.progress >= q.target;
            return (
              <div
                key={q.key}
                data-testid={`quest-row-${q.key}`}
                className="room-themed-bg"
                style={{
                  padding: '8px 12px', marginBottom: 8,
                  border: '1px solid var(--room-border, #c8c8b8)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 'bold' }}>{q.label}</span>
                  <span style={{ fontSize: 11, opacity: 0.7 }}>
                    {q.progress}/{q.target}
                    {q.reward_kills > 0 ? ` · +${q.reward_kills} kill` : ''}
                    {q.reward_hp > 0 ? ` · +${q.reward_hp} HP` : ''}
                    {q.reward_mp > 0 ? ` · +${q.reward_mp} MP` : ''}
                  </span>
                </div>
                <div style={{
                  marginTop: 6, height: 8,
                  border: '1px solid var(--room-border, #8a8a7a)',
                  background: 'var(--room-panel, #f0f0e8)',
                }}>
                  <div style={{
                    width: `${pct}%`, height: '100%',
                    background: q.claimed ? '#5a5a5a' : (done ? '#2d8a2d' : '#c9a13b'),
                    transition: 'width 400ms ease-in-out',
                  }} />
                </div>
                <div style={{ marginTop: 6, textAlign: 'right' }}>
                  {q.claimed ? (
                    <span data-testid={`quest-claimed-${q.key}`} style={{
                      display: 'inline-block', padding: '2px 8px',
                      background: '#5a5a5a', color: '#f5f5f0', fontSize: 11,
                    }}>CLAIMED</span>
                  ) : (
                    <button
                      data-testid={`quest-claim-${q.key}`}
                      disabled={!done || busyKey === q.key}
                      onClick={() => claim(q.key)}
                      style={{
                        padding: '3px 12px',
                        background: done ? '#2d6b1e' : '#c8c8b8',
                        color: '#f5f5f0',
                        border: '1px solid #1a4a10',
                        fontWeight: 'bold',
                        fontSize: 11,
                        cursor: done ? 'pointer' : 'not-allowed',
                        opacity: done ? 1 : 0.6,
                      }}
                    >{busyKey === q.key ? 'Claiming…' : (done ? 'CLAIM' : 'Locked')}</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button
            data-testid="quest-close"
            onClick={onClose}
            style={{
              padding: '4px 14px', background: '#5a4a1d', color: '#f5e6c0',
              border: '1px solid #3a2b14', fontWeight: 'bold', cursor: 'pointer',
            }}
          >Close</button>
        </div>
      </div>
    </div>
  );
}
